import { Component, EventEmitter, OnInit, Output, ViewChild } from '@angular/core';
import { Table } from 'primeng/table';
import { SpinnerService } from '../../services/spinner.service';
import { ToastService } from '../../services/toast.service';
import { SeveridadeToast } from '../../models/enums/severidade-toast.enum';
import * as ExcelJS from 'exceljs';
import { TransacaoContabil } from '../../models/transacaoContabil.model';
import { descricaoTipoTransacaoContabil, TipoTransacaoContabil } from '../../models/enums/tipoTransacaoContabil.enum';
import { JwtHelper } from '../../helpers/jwtDecode.helper';
import { AuthService } from '../../services/auth.service';
import { FormataValorPositivo } from '../../helpers/formata-valor-positivo.helper';
import { Icones } from '../../shared/variaveisDeRotas';
import { PlanoDeContasService } from '../../services/plano-de-contas.service';
import { retornaDescricao } from '../../shared/mock/descricao';
import { Location } from '@angular/common';

@Component({
  selector: 'app-razao',
  templateUrl: './razao.component.html',
  styleUrls: ['./razao.component.scss'],
})
export class RazaoComponent implements OnInit {
  @Output() tituloChange = new EventEmitter<string>();
  @Output() iconeChange = new EventEmitter<string>();

  @Output() tituloDescricao = new EventEmitter<string>();
  @Output() textoDescricao = new EventEmitter<string>();
  @Output() mostrarDescricao = new EventEmitter<boolean>();

  @ViewChild('dt') dt: Table | undefined;

  transacoes: TransacaoContabil[] = [];
  transacoesFiltradas: TransacaoContabil[] = [];
  dataInicio: Date | null = null;
  dataFim: Date | null = null;
  cnpjUsuario: string = '';
  loading: boolean = false;

  constructor(
    private planoDeContasService: PlanoDeContasService,
    private spinnerService: SpinnerService,
    private toastService: ToastService,
    private jwtHelper: JwtHelper,
    private authService: AuthService,
    public formataValorPositivoHelper: FormataValorPositivo,
    private location: Location
  ) { }

  ngOnInit() {
    this.tituloChange.emit('Relatório de Razão');
    this.iconeChange.emit(Icones.pranchetaAmarelo);
    this.tituloDescricao.emit(retornaDescricao('razao').titulo);
    this.textoDescricao.emit(retornaDescricao('razao').texto);
    this.mostrarDescricao.emit(true)

    const usuarioId = this.jwtHelper.getUserIdFromToken();
    this.authService.retornarCnpjUsuario(usuarioId).subscribe({
      next: (cnpj) => {
        this.cnpjUsuario = cnpj;
      },
      error: () => {
        this.cnpjUsuario = 'CNPJ não encontrado';
      }
    });

    const hoje = new Date();
    const primeiroDiaMesAnterior = new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1);
    const ultimoDiaMesAnterior = new Date(hoje.getFullYear(), hoje.getMonth(), 0);

    this.dataInicio = primeiroDiaMesAnterior;
    this.dataFim = ultimoDiaMesAnterior;

    this.listarRelatorioRazao();
  }

  ngOnDestroy(): void {
    this.mostrarDescricao.emit(false);
  }

  listarRelatorioRazao() {
    this.loading = true;
    this.spinnerService.show('Carregando Relatório de Razão...');

    const usuarioId = this.jwtHelper.getUserIdFromToken();

    this.planoDeContasService.listarRelatorioRazao(this.dataInicio, this.dataFim, usuarioId).subscribe({
      next: (dados) => {
        this.transacoes = dados.map(transacao => ({
          ...transacao,
          descricaoTipoTransacao: transacao.tipoTransacaoSaldoFinal === TipoTransacaoContabil.Debito ? 'Débito' : 'Crédito'
        }));

        this.transacoesFiltradas = [...this.transacoes];
        this.transacoesFiltradas = this.modificarHistoricoNfse([...this.transacoes]);
        this.loading = false;
        this.spinnerService.hide();
      },
      error: (err) => {
        this.loading = false;
        this.toastService.setMensagemToast(
          'Erro ao carregar transações!',
          SeveridadeToast.Error
        );
        this.spinnerService.hide();
      },
    });
  }

  modificarHistoricoNfse(transacoes: TransacaoContabil[]): TransacaoContabil[] {
    return transacoes.map(transacao => {
      if (transacao.origemTransacaoEnum === 2) {
        const numeroHistorico = transacao.historico.split(" - ")[0];
        return {
          ...transacao,
          historico: `${numeroHistorico} - Serviços prestados de advocacia`
        };
      }
      return transacao;
    });
  }

  onDateChange() {
    if (this.dataInicio) {
      const inicio = new Date(this.dataInicio);
      this.dataInicio = new Date(inicio.getFullYear(), inicio.getMonth(), 1);
    }

    if (this.dataFim) {
      const fim = new Date(this.dataFim);
      this.dataFim = new Date(fim.getFullYear(), fim.getMonth() + 1, 0);
    }

    if (!this.dataInicio || !this.dataFim) {
      this.toastService.setMensagemToast('Selecione as datas de início e fim.', SeveridadeToast.Info);
      return;
    }

    this.listarRelatorioRazao();
  }

  applyFilterGlobal(value: string): void {
    if (this.dt) {
      this.dt.filterGlobal(value, 'contains');
    }
  }

  voltar(): void {
    this.location.back();
  }

  downloadExcel() {
    this.spinnerService.show('Gerando relatório Excel...');
 
    let dadosParaExportar = this.transacoesFiltradas;
   
    if (this.dt && this.dt.filteredValue) {
      dadosParaExportar = this.dt.filteredValue;
    }

    this.authService.obterUsuarioPorId(this.jwtHelper.getUserIdFromToken()).subscribe({
      next: (user) => {
        const cpfCnpj = user.cpfCnpj || 'CNPJ não encontrado';

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Relatório de Razão');

        const tituloRow = worksheet.addRow(['Relatório de Razão']);
        tituloRow.font = { bold: true, underline: true };
        worksheet.mergeCells(`A${tituloRow.number}:I${tituloRow.number}`);
        tituloRow.alignment = { vertical: 'middle', horizontal: 'center' };

        const dataInicioTexto = this.dataInicio
          ? this.dataInicio.toLocaleDateString('pt-BR', { year: 'numeric', month: '2-digit' })
          : 'Nenhuma Data Selecionada';
        const dataFimTexto = this.dataFim
          ? this.dataFim.toLocaleDateString('pt-BR', { year: 'numeric', month: '2-digit' })
          : 'Nenhuma Data Selecionada';

        const cnpjRow = worksheet.addRow(['CNPJ:', cpfCnpj]);
        const inicioRow = worksheet.addRow(['Período Início:', dataInicioTexto]);
        const fimRow = worksheet.addRow(['Período Fim:', dataFimTexto]);

        [cnpjRow, inicioRow, fimRow].forEach(row => {
          row.getCell(1).font = { bold: true };
        });

        if (this.dt && this.dt.filteredValue && this.dt.filteredValue.length !== this.transacoesFiltradas.length) {
          const filtroRow = worksheet.addRow(['Filtro de Pesquisa:', 'Aplicado']);
          filtroRow.getCell(1).font = { bold: true };
          const registrosRow = worksheet.addRow(['Registros Exportados:', `${dadosParaExportar.length} de ${this.transacoesFiltradas.length}`]);
          registrosRow.getCell(1).font = { bold: true };
        }

        worksheet.addRow([]);

        const titleRow = worksheet.addRow([
          'Conta Contábil', 'Data', 'Histórico', 'Débito', 'Crédito', 'Saldo', 'Natureza', 'Conta de Contrapartida', 'Nº Lançamento'
        ]);

        titleRow.font = { bold: true };
        titleRow.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFD3D3D3' },
        };

        worksheet.columns = [
          { key: 'contaContabilDescricao', width: 25 },
          { key: 'dataTransacao', width: 12 },
          { key: 'historico', width: 35 },
          { key: 'debito', width: 12 },
          { key: 'credito', width: 12 },
          { key: 'valorFinal', width: 12 },
          { key: 'tipoTransacao', width: 10 },
          { key: 'contaContrapartida', width: 20 },
          { key: 'lancamento', width: 12 },
        ];

        dadosParaExportar.forEach((transacao, index) => {
          const row = worksheet.addRow({
            contaContabilDescricao: transacao.contaContabil?.descricao,
            dataTransacao: new Date(transacao.dataTransacao).toLocaleDateString('pt-BR'),
            historico: transacao.historico,
            debito: Math.abs(Number(transacao.debito) || 0),
            credito: Math.abs(Number(transacao.credito) || 0),
            valorFinal: Math.abs(Number(transacao.valorFinal) || 0),
            tipoTransacao: transacao.valorFinal !== 0 ? (transacao.tipoTransacaoSaldoFinal === TipoTransacaoContabil.Debito ? 'D' : 'C') : '',
            contaContrapartida: transacao.contaContrapartida,
            lancamento: transacao.lancamento
          });
          row.getCell(4).numFmt = '#,##0.00'; 
          row.getCell(5).numFmt = '#,##0.00'; 
          row.getCell(6).numFmt = '#,##0.00'; 
        });



        workbook.xlsx.writeBuffer().then((buffer) => {
          const blob = new Blob([buffer], { type: 'application/octet-stream' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          const dataHora = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
          a.download = `Relatório-Razão-${dataHora}.xlsx`;
          
          a.click();
          window.URL.revokeObjectURL(url);

          this.spinnerService.hide();
          this.toastService.setMensagemToast('Relatório de razão gerado com sucesso!', SeveridadeToast.Success);
        }).catch((error) => {
          console.error('Erro ao gerar Excel:', error);
          this.spinnerService.hide();
          this.toastService.setMensagemToast('Erro ao gerar relatório de razão.', SeveridadeToast.Error);
        });
      },
      error: (err) => {
        this.spinnerService.hide();
        this.toastService.setMensagemToast('Erro Interno.', SeveridadeToast.Error);
      }
    });
  }
}
